/**
 * @file catalog-more.ts
 * @description Заготовки секций платформы, вторая половина библиотеки
 * (phase19.16 Р2; SB25–SB27; uiux-design-r2 §5): призывы, отзывы, вопросы,
 * контакты, подписка и пустые блоки на 1–4 колонки. Правила те же, что в
 * catalog.ts: нейтрально, данные — из каталога, отзывов и настроек сайта.
 * «Строка объявлений» (решение пользователя 2026-10-09) — в группе «Призыв».
 */

import { defaultSectionLook } from "../defaults.js";
import { el, header, section } from "./build.js";
import { GENERAL_PRESET_CONTEXTS, type SectionPreset } from "./types.js";

const center = { align: "center" as const };

function preset(
  p: Omit<SectionPreset, "previewKey" | "backgroundPhoto" | "requires" | "contexts"> & Partial<SectionPreset>,
): SectionPreset {
  return { previewKey: p.key, backgroundPhoto: false, requires: null, contexts: GENERAL_PRESET_CONTEXTS, ...p };
}

const CTA: SectionPreset[] = [
  preset({
    key: "cta-messenger",
    group: "cta",
    title: "Призыв: написать в мессенджер",
    hint: "Вопросы, заказ под себя",
    build: () =>
      section({
        columns: [
          [
            el("heading", { level: 2, text: "Остались вопросы?", ...center }),
            el("text", { text: "Напишите нам — ответим и поможем выбрать.", ...center }),
            // Мессенджеры — из «Настроек сайта» (SB25): вписать адрес в блок нельзя.
            el("contacts", { kinds: ["telegram", "whatsapp", "vk", "max"], ...center }),
          ],
        ],
      }),
  }),
  preset({
    key: "cta-color",
    group: "cta",
    title: "Призыв с кнопкой на цвете",
    hint: "Акция, переход в каталог",
    build: () =>
      section({
        look: { ...defaultSectionLook(), bg: { kind: "role", role: "darkBg" } },
        columns: [
          [
            el("heading", { level: 2, text: "Короткий призыв", ...center }),
            el("text", { text: "Одно предложение о том, зачем нажать кнопку.", ...center }),
            el("button", {
              label: "Перейти в каталог",
              target: { type: "system", route: "CATALOG" },
              icon: "arrow",
              ...center,
            }),
          ],
        ],
      }),
  }),
  preset({
    key: "announcements-strip",
    group: "cta",
    title: "Строка объявлений",
    hint: "Действующие акции магазина — сменяются по очереди",
    build: () =>
      section({
        look: {
          ...defaultSectionLook(),
          bg: { kind: "role", role: "accent" },
          // Тонкая полоса: снаружи 0, внутри XS (8 px), на телефоне вплотную (как v2 «XS, плотнее»).
          space: {
            outerTop: 0,
            outerBottom: 0,
            innerTop: 8,
            innerBottom: 8,
            phone: { innerTop: 0, innerBottom: 0 },
            overlap: 0,
            mergeWithPrev: false,
          },
        },
        columns: [
          [
            // Акции из раздела «Акции» (SB25); нет акций — секция скрыта (SB21).
            el("announcements", {
              source: "promotions",
              items: [],
              limit: 5,
              promoTextMode: "auto",
              intervalSec: 6,
              ...center,
            }),
          ],
        ],
      }),
  }),
];

const DATA: SectionPreset[] = [
  preset({
    key: "reviews",
    group: "reviews",
    title: "Отзывы покупателей",
    hint: "Доверие на главной и в товаре",
    build: () =>
      section({
        header: header("Отзывы покупателей"),
        columns: [[el("reviews", { source: "product", pick: "best", limit: 6, layout: "carousel" })]],
      }),
  }),
  preset({
    key: "faq",
    group: "faq",
    title: "Вопросы и ответы",
    hint: "Доставка, оплата, уход за товаром",
    build: () =>
      section({
        header: header("Вопросы и ответы"),
        look: { ...defaultSectionLook(), container: 768 },
        columns: [
          [
            el("faq", {
              items: [
                { q: "Как быстро вы отправляете заказ?", a: "Напишите здесь срок сборки и отправки." },
                { q: "Как можно оплатить?", a: "Перечислите способы оплаты, которые принимаете." },
                { q: "Можно ли вернуть товар?", a: "Коротко опишите условия возврата." },
              ],
              firstOpen: true,
            }),
          ],
        ],
      }),
  }),
  preset({
    key: "contacts",
    group: "contacts",
    title: "Контакты",
    hint: "Как связаться и где забрать заказ",
    build: () =>
      section({
        valign: "top",
        columns: [
          [
            el("heading", { level: 2, text: "Контакты" }),
            el("text", { text: "Пишите и звоните — ответим в рабочее время." }),
          ],
          [el("contacts", { kinds: ["phone", "email", "telegram", "address"] })],
        ],
      }),
  }),
  preset({
    key: "newsletter",
    group: "newsletter",
    title: "Подписка",
    hint: "Новости и акции на почту",
    requires: "newsletter",
    build: () =>
      section({
        look: { ...defaultSectionLook(), bg: { kind: "role", role: "panelBg" } },
        columns: [
          [
            el("heading", { level: 2, text: "Подпишитесь на новости", ...center }),
            el("text", { text: "Пишем о новинках и акциях — не чаще раза в неделю.", ...center }),
            el("newsletter", center),
          ],
        ],
      }),
  }),
];

const EMPTY_TITLES = ["1 колонка", "2 колонки", "3 колонки", "4 колонки"] as const;

const EMPTY: SectionPreset[] = EMPTY_TITLES.map((title, index) =>
  preset({
    key: `empty-${index + 1}`,
    group: "empty",
    title: `Пустой блок: ${title}`,
    hint: "Соберите сами из элементов",
    build: () => section({ valign: "top", columns: Array.from({ length: index + 1 }, () => []) }),
  }),
);

export const SECTION_PRESETS_CTA_TO_EMPTY: readonly SectionPreset[] = [...CTA, ...DATA, ...EMPTY];
